import { useState, useEffect, lazy, Suspense } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { UserPlus, Settings, Menu as MenuIcon, LogOut, ArrowLeft, Loader2 } from 'lucide-react';
import { Button, LanguageSwitcher, Tooltip } from '../ui';
import logo from '../../assets/logo.png';
import { useLanguage } from '../../context/LanguageContext';
import NotificationBell from '../notifications/NotificationBell';
import { getStoredUser } from '../../context/AuthContext';
import { getSettingValue } from '../../services/settingsService';

// Lazy load AddContactModal (1100+ lines, only needed on click)
const AddContactModal = lazy(() => import('../contacts/AddContactModal'));

/**
 * Header Component
 * Displays the Potoo logo with baseline on the left and add contact button on the right
 * @param {Object} props
 * @param {Function} props.onContactAdded - Callback when a contact is added
 * @param {Function} props.onToggleMenu - Function to toggle mobile menu
 * @param {boolean} props.showMenuButton - Whether to show the menu button
 * @param {boolean} props.isSidebarEnabled - Whether sidebar is enabled
 */
const Header = ({ onContactAdded, onToggleMenu, onLogout, onNavigate, showMenuButton = false, isSidebarEnabled = true }) => {
    const { t, language, showLanguageSwitcher } = useLanguage();
    const [isAddContactModalOpen, setIsAddContactModalOpen] = useState(false);
    const [slogan, setSlogan] = useState('');
    const navigate = useNavigate();
    const location = useLocation();
    const isHomePage = location.pathname === '/' || location.pathname === '';

    // Load slogan from settings API
    useEffect(() => {
        getSettingValue('SLOGAN', language).then(value => {
            if (value) setSlogan(value);
        });
    }, [language]);

    const handleBack = () => {
        if (window.history.length > 1) {
            navigate(-1);
        } else {
            navigate('/');
        }
    };

    // Get current user from localStorage using the hook
    const storedUser = getStoredUser();
    const currentUser = {
        id: storedUser?.id || parseInt(localStorage.getItem('userId') || '0', 10),
        uuid: storedUser?.peopleUuid || localStorage.getItem('userUuid') || '',
        email: storedUser?.email || localStorage.getItem('userEmail') || '',
        name: storedUser ? `${storedUser.firstName} ${storedUser.lastName}`.trim() : localStorage.getItem('userName') || ''
    };

    return (
        <div className="flex items-center justify-between mb-4 gap-2">
            {/* Left side: hamburger + preferences (mobile) + logo */}
            <div className="flex items-center gap-2 min-w-0">
                {/* Mobile menu button (navigates to home if sidebar disabled) */}
                {showMenuButton && (
                    <button
                        onClick={onToggleMenu}
                        className={`flex-shrink-0 w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center text-cabaza-purple hover:bg-gray-200 transition-colors ${isSidebarEnabled ? 'sm:hidden' : ''}`}
                        aria-label={isSidebarEnabled ? "Ouvrir le menu" : "Accueil"}
                    >
                        <MenuIcon className="w-6 h-6" />
                    </button>
                )}
                {/* Preferences button - mobile only (left of logo) */}
                <button
                    onClick={() => onNavigate('preferences')}
                    className="sm:hidden flex-shrink-0 p-2 rounded-full hover:bg-gray-100 transition-colors text-gray-500 hover:text-cabaza-purple"
                    title={t.menu?.preferences || 'Préférences'}
                >
                    <Settings className="w-5 h-5" />
                </button>
                {!isHomePage && (
                    <button
                        onClick={handleBack}
                        className="flex-shrink-0 w-10 h-10 mr-1 rounded-full bg-gray-100 flex items-center justify-center text-cabaza-purple hover:bg-gray-200 transition-colors print:hidden"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                )}
                <div className="flex flex-col items-start">
                    <img
                        src={logo}
                        alt="Potoo"
                        className="h-10 sm:h-15 object-contain object-left flex-shrink-0"
                    />
                    <span className="hidden sm:block text-xs sm:text-sm font-semibold whitespace-nowrap bg-gradient-to-r from-cabaza-purple to-cabaza-pink bg-clip-text text-transparent mt-1">
                        {slogan || t.landing?.slogan || 'Le réseau social européen pour échanger entre Cabazats'}
                    </span>
                </div>
            </div>

            {/* Right side: icons row + add contact below */}
            <div className="flex flex-col items-end gap-1 flex-shrink-0">
                {/* Icons row */}
                <div className="flex items-center gap-2 sm:gap-3">
                    {showLanguageSwitcher && <LanguageSwitcher variant="compact" />}
                    <NotificationBell onNavigate={onNavigate} />
                    {/* Preferences button - desktop only (next to bell) */}
                    <button
                        onClick={() => onNavigate('preferences')}
                        className="hidden sm:block p-2 rounded-full hover:bg-gray-100 transition-colors text-gray-500 hover:text-cabaza-purple"
                        title={t.menu?.preferences || 'Préférences'}
                    >
                        <Settings className="w-5 h-5" />
                    </button>
                    <button
                        onClick={onLogout}
                        className="p-2 rounded-full hover:bg-gray-100 transition-colors text-gray-500 hover:text-red-500"
                        title={t.menu?.logout || 'Déconnexion'}
                    >
                        <LogOut className="w-5 h-5" />
                    </button>
                </div>
                {/* Add contact button */}
                <Tooltip text={t.dashboard?.addContact || 'Ajouter un contact'} position="bottom-left">
                    <Button onClick={() => setIsAddContactModalOpen(true)} icon={UserPlus} />
                </Tooltip>
            </div>

            {/* Add Contact Modal - Lazy loaded */}
            {isAddContactModalOpen && (
                <Suspense fallback={
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                        <Loader2 className="w-8 h-8 animate-spin text-white" />
                    </div>
                }>
                    <AddContactModal
                        isOpen={isAddContactModalOpen}
                        onClose={() => setIsAddContactModalOpen(false)}
                        currentUser={currentUser}
                        onContactAdded={(contact) => {
                            onContactAdded?.(contact);
                            setIsAddContactModalOpen(false);
                        }}
                    />
                </Suspense>
            )}
        </div>
    );
};

export default Header;
